import { IkeToken } from '@ikegps/core-api';
import { action, makeObservable } from 'mobx';
import { MaterialIcon } from '../components/icon/icons';
import { addStyles } from '../components/theme';
import { T } from '../i18n';
import { m, type MithrilVNode } from '../mithril';
import { CollectionEdit } from '../modules/collection.view/collection.view';
import { CollectionEditCtrl } from '../modules/collection.view/collection.view.ctrl';
import { CSS as CollectionViewCSS } from '../modules/collection.view/collection.view.style';
import { TagList } from '../modules/tag.list/tag.list';
import { CSS as TagListCSS } from '../modules/tag.list/tag.list.style';
import { BoundingString } from '../modules/tag.map/bounding.string';
import { TagMap } from '../modules/tag.map/tag.map';
import { Logger, Services } from '../services';
import { InputEventBindContext } from '../services/input/event.context';
import type { EventData, Modifiers } from '../services/input/input.service';
import { CurrentState } from '../state/page.state';
import { AppStore } from '../store';
import { BrowserCheck } from '../util/browser-check';
import { IkePage, type IkePageConfig, IkePageName } from './page';
import { TagState } from './tag.state';

export const CSS = {
	container: 'c-TagView',
};

export const Style = {
	[CSS.container]: {
		display: 'flex',
		height: '100%',
		'min-width': '1000px',
		[`& .${TagListCSS.container}`]: {
			width: '20%',
			'min-width': '350px',
		},
		[`& .${CollectionViewCSS.container}`]: {
			width: '20%',
			'min-width': '420px',
		},
	},
};

addStyles(Style);

export const TagMapPageConfig: IkePageConfig = {
	type: 'internal',
	name: IkePageName.map,
	path: ['/map', '/map/:location'],
	auth: {
		required: true,
	},
	nav: {
		name: T.LIST_MAP,
		icon: MaterialIcon.MAP,
	},
	layout: { nav: false },
	supportDepartmentChange: true,
};

export class TagMapPage extends IkePage {
	state: TagState;
	tagList: TagList;
	tagMap: TagMap;
	eventContext: InputEventBindContext;
	browserCheck: BrowserCheck;

	editCtrl: CollectionEditCtrl;
	collectionView: CollectionEdit;

	rendered: boolean = false;

	constructor() {
		super(TagMapPageConfig);
		makeObservable(this);

		this.browserCheck = new BrowserCheck();
	}

	private init() {
		// TODO: Move this into routing logic
		AppStore.TagSummary.clear();
		Services.Search.reset();
		const refreshTagPromise = AppStore.Tag.refresh();
		CurrentState.reset();

		this.state = new TagState();
		this.tagList = new TagList(this.state);
		this.tagMap = new TagMap(this.state, 'tagMap');

		this.editCtrl = new CollectionEditCtrl(this.state.map);
		this.collectionView = new CollectionEdit(this.editCtrl);

		Promise.resolve()
			.then(() => refreshTagPromise)
			.then(() => this.syncInspectedCollection())
			.then(() => this.syncSearchFilter())
			.catch((e) => Logger.error(e));

		this.reaction(
			() => Services.Search.filterState.filter,
			(value) => {
				Services.Route.setParam('f', value);
				Services.Analytics.trackEvent(Services.Analytics.EventCategory.BigMap, 'Filter', value, 1);
			},
			{ delay: Services.Analytics.DEBOUNCE_TIME }
		);

		this.reaction(
			() => CurrentState.inspectedCollectionRevision,
			(revision) => {
				const collectionSummary = AppStore.CollectionSummary.get(revision);
				if (collectionSummary == null) {
					return;
				}
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				this.state.map.zoomToCollection(collectionSummary.revision);
				Services.Route.setParam('t', CurrentState.inspectedTagId);
				Services.Route.setParam('collection', collectionSummary.uuid);

				Services.Analytics.trackEvent(
					Services.Analytics.EventCategory.BigMap,
					'Inspect:Collection',
					collectionSummary.uuid,
					1
				);
			},
			{ delay: Services.Analytics.DEBOUNCE_TIME }
		);

		// Watch the bounds and update the location
		this.reaction(
			(_) => {
				return {
					center: this.state.map.bounds && this.state.map.bounds.getCenter().toJSON(),
					zoom: this.state.map.zoom,
				};
			},
			(args) => {
				const boundingString = BoundingString.toString(args.center, args.zoom);
				if (boundingString === '') {
					return;
				}

				Services.Route.setParam('location', boundingString);
				Services.Analytics.trackEvent(Services.Analytics.EventCategory.BigMap, 'Bounds', boundingString, 1);
			},
			{ delay: Services.Analytics.DEBOUNCE_TIME }
		);

		this.eventContext = Services.Input.getContext();
		this.eventContext.on(this.eventContext.Code.KeyS, this.saveCollection, {
			modifiers: {
				ctrl: true,
			},
		});

		if (
			Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE) ||
			Services.Token.has(IkeToken.FEATURE_CTRL_SHIFT_SELECT)
		) {
			if (this.browserCheck.os === 'Mac OS') {
				// macos
				this.registerCtrlShiftPressed('keydown', true, { meta: true, shift: true });
				this.registerCtrlShiftPressed('keyup', false, { meta: true });
			}

			// windows
			this.registerCtrlShiftPressed('keydown', true, { ctrl: true, shift: true });
			this.registerCtrlShiftPressed('keyup', false, { ctrl: true });

			// common
			this.registerCtrlShiftPressed('keyup', false, { shift: true });
		}

		this.rendered = true;
	}

	get nodes(): MithrilVNode {
		if (!Services.Map.isReady) {
			return null;
		}

		if (Services.Department.current == null) {
			AppStore.TagSummary.clear();
			return null;
		}

		if (this.rendered === false) {
			this.init();
		}

		const showInspectedCollection = CurrentState.inspectedCollectionRevision != null;
		return m('div', { className: CSS.container }, [
			this.tagList.view(),
			this.tagMap.view(),
			showInspectedCollection ? this.collectionView.view() : null,
		]);
	}

	override onRemove() {
		super.onRemove();
		if (this.state != null) {
			this.state.destroy();
		}

		if (this.eventContext) {
			this.eventContext.destroy();
		}

		if (this.editCtrl) {
			this.editCtrl.destroy();
		}
	}

	@action
	private syncSearchFilter() {
		if (Services.Route.params.get('f') != null) {
			Services.Search.inputfilterState.setFilter(Services.Route.params.get('f'));
			return;
		}

		const filter = Services.Search.inputfilterState.filter;
		if (filter != null) {
			Services.Route.setParam('f', filter);
		}
	}

	@action.bound saveCollection(evt: EventData) {
		if (this.editCtrl.collection == null) {
			return;
		}

		evt.event.preventDefault();
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.editCtrl.saveChanges(evt.event);
	}

	private async syncInspectedCollection() {
		const tagId = Services.Route.params.get('t');
		const collectionUUID = Services.Route.params.get('collection');

		if (tagId == null || collectionUUID == null) {
			return Promise.resolve();
		}

		await AppStore.Tag.fetch(tagId);
		const tagSummary = await AppStore.TagSummary.getFetchable(tagId).fetch();
		if (tagSummary == null) {
			CurrentState.setInspectedCollectionFailed();
			return null;
		}

		const promises = tagSummary.revisions.map((revision) => AppStore.CollectionSummary.fetch(revision));
		const collectionSummaries = await Promise.all(promises);

		if (collectionSummaries == null) {
			return;
		}

		const targetCollectionSummary = collectionSummaries.find((c) => c.uuid === collectionUUID);
		if (targetCollectionSummary == null) {
			CurrentState.setInspectedCollectionFailed();
			return;
		}

		CurrentState.setInspectedCollection(targetCollectionSummary.revision);
	}

	private registerCtrlShiftPressed(eventType: 'keydown' | 'keyup', isPressed: boolean, modifiers: Partial<Modifiers>) {
		this.eventContext.on(
			this.eventContext.Code.AllKeys,
			() => {
				CurrentState.setIsCtrlShiftPressed(isPressed);
			},
			{
				eventType,
				modifiers,
			}
		);
	}
}
